import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";

// Validation schema (basic example — you can expand this)
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { StoreIpdConsultantChange } from "../../../../api/ipd/ipdConsultantChangeAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

const schema = yup.object().shape({
    ipdEntryId: yup.string().required("IPD is required"),
    oldConsultantId: yup.string().required("Old Consultant is required"),
    newConsultantId: yup.string().required("New Consultant is required"),
});


const AddIpdConsultantChange = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [ipdEntries, setIpdEntries] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [patientDetails, setPatientDetails] = useState({});

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {

        }
    });

    const fetchIpdEntries = async () => {
        try {
            const response = await GetIpdEntries();
            if (response.success) {
                setIpdEntries(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchConsultants = async () => {
        try {
            const response = await GetConsultants();
            if (response.success) {
                setConsultants(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    useEffect(() => {
        fetchIpdEntries();
        fetchConsultants();
    }, []);


    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });
            const response = await StoreIpdConsultantChange(data);

            if (response.success) {
                showToast("success", response.message);
                navigate(`/ipd/transaction/ipd-consultant-change/list`);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

    return (
        <Fragment>
            <Breadcrumb parent="Ipd Consultant Change" title="Add Ipd Consultant Change" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h5>Add Ipd Consultant Change</h5>
                                <Link
                                    to={`${process.env
                                        .PUBLIC_URL}/ipd/transaction/ipd-consultant-change/list`}
                                >
                                    <button className="btn btn-primary" id="list">
                                        List Ipd Consultant Change
                                    </button>
                                    <UncontrolledTooltip placement="top" target="list">
                                        List Ipd Consultant Change
                                    </UncontrolledTooltip>
                                </Link>
                            </div>

                            <form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">

                                    {/* ================= IPD & BASIC ================= */}
                                    <div className="row">
                                        <div className="col-6">
                                            <label>IPD No</label>
                                            <select className="form-control" {...register("ipdEntryId", {
                                                onChange: (e) => {
                                                    setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                                                    setValue("oldConsultantId", ipdEntries.find(ipd => ipd._id === e.target.value)?.consultantDoctorId?._id)
                                                }
                                            })}>
                                                <option value="">Select IPD</option>
                                                {ipdEntries.map(ipd => (
                                                    <option key={ipd._id} value={ipd._id}>
                                                        {ipd.ipdNo}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6 mt-3">
                                            <label>Old Consultant</label>
                                            <select className="form-control" {...register("oldConsultantId")} readOnly>
                                                <option value="">Select Consultant</option>
                                                {consultants.map((item) => (
                                                    <option value={item._id}>{item.name}</option>
                                                ))}
                                            </select>
                                            {errors.oldConsultantId && <span className="text-danger">{errors.oldConsultantId.message}</span>}
                                        </div>
                                        <div className="col-6 mt-3">
                                            <label>New Consultant</label>
                                            <select className="form-control" {...register("newConsultantId")}>
                                                <option value="">Select Consultant</option>
                                                {consultants.map((item) => (
                                                    <option value={item._id}>{item.name}</option>
                                                ))}
                                            </select>
                                            {errors.newConsultantId && <span className="text-danger">{errors.newConsultantId.message}</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer text-end">
                                    <button type="submit" className="btn btn-primary">
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default AddIpdConsultantChange;
